import { gql, useLazyQuery } from "@apollo/client";
import {
  Checkbox,
  IIconProps,
  Label,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import React, { useContext, useEffect, useState } from "react";
import AppContext, { IFilters } from "../../../AppContext";
import Title from "../../../components/Title";
import Notification from "../../../components/Notification";
import "./style.scss";
import { useTranslation } from "react-i18next";


export const title = "Descarga masiva de datos";
export const filters: IFilters = {
  limitedPeriodo: false,
  showMedidas: false,
  hasTicketMedio: false,
  isDesglose: false,
  showEntorno: false,
  showSegmentos: false,
  showMyData: false,
  onlyYears: true,
};

const GET_COMPARABLES_EXCEL = gql`
  query ($year: Int, $wk1: Int, $wk2: Int, $format: String) {
    getComparablesExcel(
      WeekRange: {
        Start: { Year: $year, Week: $wk1 }
        End: { Year: $year, Week: $wk2 }
      }
      Filters: { Format: $format }
    )
  }
`;

const Graph = ({ admin }: { admin?: boolean }) => {
  const{t, i18n}=useTranslation();
  const { semanas, filters } = useContext(AppContext);
  const [data, setData] = useState<any>([]);
  //const [reloading, setReloading] = useState(false)
  const [links, setLinks] = useState<string[]>([]);
  const [format, setFormat] = useState<string>("XLSX");
  const [horizontal, setHorizontal] = useState(false);

  //Comparables Excel 
  const [compState, setCompState] = useState<"idle" | "done" | "error">("idle");
  const [compLink, setCompLink] = useState<string | null>(null);
  const [compFormat, setCompFormat] = useState<"XLSX" | "CSV">("XLSX");

  const [getComparablesExcel, { loading: compLoading }] = useLazyQuery(
    GET_COMPARABLES_EXCEL,
    {
      fetchPolicy: "no-cache",
              onCompleted: (d) => {
          let raw = d?.getComparablesExcel;
          const result = typeof raw === "string" ? JSON.parse(raw) : raw;
          if (!result || result?.Error || !result?.Data) {
            setCompState("error");
            return;
          }
          // Dispara la descarga directamente aquí
          const byteArray = Uint8Array.from(atob(result.Data), c => c.charCodeAt(0));
          const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = result.Filename ?? `Comparables.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
          setCompState("done");
        },
      onError: () => setCompState("error"),
    }
  );

  const GET_GRAPH = gql`
    query (
      $yr1: Int!
      $yr2: Int!
      $var: String!
      $format: String!
      $alternativo: Boolean
      $lang: String
    ) {
      graph(
        Graph: "Descarga masiva"
        WeekRange: {
          Start: { Year: $yr1, Week: 0 }
          End: { Year: $yr2, Week: 0 }
        }
        Filters: { Segmento: $var, Format: $format, Alternativo: $alternativo, Lang: $lang }
      )
    }
  `;

  const [getData, { loading, error }] = useLazyQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
    onCompleted: (fetchedData) => {
      setData(fetchedData);
      if (fetchedData?.graph?.Link.length > 0)
        setLinks(fetchedData?.graph?.Link);
    },
  });

  useEffect(() => {
    setData([]);
    setCompState("idle");
    setCompLink(null);
  }, [filters, semanas]);

  let errorMsg = t('An-error-has-occur-when-recovering-data');
  //let loading=(qry.loading || qry.data===undefined)
  //let error=(!semanas || !!qry.error)
  let emptyData = {
    Total: {
      Ventas: { Total: 0, Tendencia: 0 },
      Tickets: { Total: 0, Tendencia: 0 },
      TicketMedio: { Total: 0, Tendencia: 0 },
      PuntosDeVenta: { Total: 0, Tendencia: 0 },
      VentaMediaLocal: { Total: 0, Tendencia: 0 },
    },
    Comparable: {
      Ventas: { Total: 0, Tendencia: 0 },
      Tickets: { Total: 0, Tendencia: 0 },
      TicketMedio: { Total: 0, Tendencia: 0 },
      PuntosDeVenta: { Total: 0, Tendencia: 0 },
      VentaMediaLocal: { Total: 0, Tendencia: 0 },
    },
    Aperturas: { Total: 0, Tendencia: 0 },
    Error: false,
  };
  let graph = emptyData;
  let isError = !semanas || !!error;
  const noData = data.length === 0;

  if (!noData) {
    try {
      graph = data?.graph;
      if (!graph) throw new Error(errorMsg);
      if (graph?.Error) throw new Error(`${data.Error}`);
    } catch (err: any) {
      isError = true;
      errorMsg =
        err.message !== "undefined" ? err.message : errorMsg ?? errorMsg;
    }
  }

  const downloadIcon: IIconProps = { iconName: "Download" };
  const excelIcon: IIconProps = { iconName: "ExcelDocument" };
  const csvIcon: IIconProps = { iconName: "TextDocument" };
  const [showNotification, { toggle: toggleNotification }] = useBoolean(true);
  const comparableYear = semanas?.[1]?.year ?? semanas?.[0]?.year;
  const comparablePrevYear = comparableYear ? comparableYear - 1 : "";

  let cSegmento = () => {
    if (filters.variables === 0) return "el total de los segmentos";
    let name = filters.segmentos[filters.variables];
    if (name.slice(-1) !== name.slice(-1).toUpperCase())
      name = name.toLowerCase();
    return `el segmento ${name}`;
  };

  const handleDetails = (_: any, checked: boolean | undefined) => {
    setHorizontal(!!checked);
  };

  return (
    <>
      <Title title={t('Data-mass-data-load')} />
      {isError && (
        <Notification
          text={errorMsg}
          type={"error"}
          visible={showNotification}
          toggleVisibility={toggleNotification}
          autoHide={false}
        />
      )}
      <div style={{ display: "flex", justifyContent: "center", gap: 32, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div
          className="modal-container dark in-section"
          style={{
            width: "unset",
            height: "unset",
            padding: "unset",
            background: "unset",
            position: "relative",
          }}
        >
          <div
            className="table-container"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <div
              className="table"
              style={{
                padding: 40,
                margin: "0 10px",
                width: 470,
                minWidth: 280,
              }}
            >
              <div className="container" style={{ textAlign: "center" }}>
                {/*reloading && <Spinner label={"Recargando..."} size={SpinnerSize.large} />*/}
                {(loading || (!loading && !noData)) && (
                  <>
                    {loading && (
                      <Spinner
                        label={t('Generating-report')}
                        size={SpinnerSize.large}
                      />
                    )}
                    {!loading && (
                      <>
                        <Label style={{ paddingBottom: "2em" }}>
                          {isError
                            ? t('Report-not-generated')
                            : t('Report-generated-successfully')}
                          .
                        </Label>
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                          <div className="generate-buttons">
                            {links.map((link, i) => {
                              // if cad es undefined, a regex that gets com_ and 4 numbers from link
                              let com = link.match(/com_\d{4}/)?.[0];
                              // year
                              // get año and 4 numbers from link
                              let year = link.match(/Año \d{4}/)?.[0];

                              return (
                                <PrimaryButton
                                  key={i}
                                  iconProps={downloadIcon}
                                  text={
                                    format === "XLSX"
                                      ? t('Download-report')
                                      : `${com} - ${year} - ${link
                                          .split(" ")[14]
                                          .replace(".xlsx", "")
                                          .replace(".csv", "")}`
                                  }
                                  href={`${window.location.href}${link}`}
                                  download={true}
                                  allowDisabledFocus
                                />
                              );
                            })}
                          </div>
                        </Stack>
                      </>
                    )}
                  </>
                )}
                {!loading && noData && (
                  <>
                    <Label style={{ paddingBottom: "2em" }}>
                    {t("Report-generated-data", {
    year1: semanas[1].year,
    year2:
      semanas[0].year === semanas[1].year
        ? semanas[0].year - 1
        : semanas[0].year,
  })}
                    </Label>
                    <Stack horizontal tokens={{ childrenGap: 5 }}>
                      <div className="generate-buttons vertical-buttons">
                        <PrimaryButton
                          iconProps={excelIcon}
                          text={t('Generate-xlsx')}
                          onClick={() => {
                            setFormat("XLSX");
                            getData({
                              variables: {
                                yr1: semanas[0].year,
                                yr2: semanas[1].year,
                                var: filters.segmentos[filters.variables],
                                alternativo: horizontal ?? false,
                                format: "XLSX",
                                lang: (i18n.language || 'es').slice(0,2)
                              },
                            });
                          }}
                          allowDisabledFocus
                        />
                        <PrimaryButton
                          iconProps={csvIcon}
                          text={t('Generate-csv')}
                          onClick={() => {
                            setFormat("CSV");
                            getData({
                              variables: {
                                yr1: semanas[0].year,
                                yr2: semanas[1].year,
                                var: filters.segmentos[filters.variables],
                                alternativo: horizontal ?? false,
                                format: "CSV",
                                lang: (i18n.language || 'es').slice(0,2)
                              },
                            });
                          }}
                          allowDisabledFocus
                        />
                      </div>
                    </Stack>
                    {/*!!admin && (
                      <div className="check-container-small">
                        <Checkbox
                          id={"hrz-percentage"}
                          label={
                            "Descargar con formato de porcentajes horizontales"
                          }
                          className="ms-Checkbox-Filters"
                          checked={horizontal}
                          onChange={handleDetails}
                        />
                      </div>
												)*/}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Comparables Excel */}
        {/* <div
          className="modal-container dark in-section"
          style={{
            width: "unset",
            height: "unset",
            padding: "unset",
            background: "unset",
            position: "relative",
          }}
        >
          <div
            className="table-container"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <div
              className="table"
              style={{
                padding: 40,
                margin: "0 10px",
                width: 470,
                minWidth: 280,
              }}
            >
              <div className="container" style={{ textAlign: "center" }}>
                {compLoading && (
                  <Spinner label={t("Generating-comparables")} size={SpinnerSize.large} />
                )}

                  {!compLoading && compState === "done" && (
                    <Label style={{ paddingBottom: "2em" }}>
                      {t("Comparable-generated-successfully")}
                    </Label>
                  )}

                {!compLoading && compState === "error" && (
                  <Label style={{ paddingBottom: "2em", color: "red" }}>
                    {t("Comparable-error")}
                  </Label>
                )}

                {!compLoading && compState === "idle" && (
                  <>
                    <Label style={{ paddingBottom: "2em" }}>
                      {t("Comparable-report-data", {
                        year1: comparableYear ?? "",
                        year2: comparablePrevYear,
                      })}
                    </Label>
                    <Stack horizontal tokens={{ childrenGap: 5 }}>
                      <div className="generate-buttons">
                        <PrimaryButton
                          iconProps={{ iconName: "ExcelDocument" }}
                          text={t("Generate-comparables-xlsx")}
                          disabled={!comparableYear}
                          onClick={() => {
                            setCompFormat("XLSX");
                            setCompLink(null);
                            getComparablesExcel({
                              variables: {
                                year: comparableYear,
                                wk1: 1,
                                wk2: 53,
                                format: "XLSX",
                              },
                            });
                          }}
                          allowDisabledFocus
                        />
                        <PrimaryButton
                          iconProps={{ iconName: "TextDocument" }}
                          text={t("Generate-comparables-csv")}
                          disabled={!comparableYear}
                          onClick={() => {
                            setCompFormat("CSV");
                            setCompLink(null);
                            getComparablesExcel({
                              variables: {
                                year: comparableYear,
                                wk1: 1,
                                wk2: 53,
                                format: "CSV",
                              },
                            });
                          }}
                          allowDisabledFocus
                        />
                      </div>
                    </Stack>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>  */}
      </div> 
    </>
  );
};

export default Graph;